import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { chatService } from '../../services/chat';
import { analytics } from '../../services/analytics';
import { EventActions, EventCategories } from '../../config/analytics';
import {
  InterviewSession,
  ChatMessage as ChatMessageType,
  ChatRequest,
  InterviewSessionStatus,
} from '../../types/chat';
import { Button } from '../ui/Button';

interface InterviewChatProps {
  session: InterviewSession;
  onSessionUpdate: (session: InterviewSession) => void;
  onSessionEnd: (summary: any) => void;
  /** Anonymous /try-interview demo — uses guest API and hides full session end flow */
  guestMode?: boolean;
  onGuestQuotaExhausted?: () => void;
}

export const InterviewChat: React.FC<InterviewChatProps> = ({
  session,
  onSessionUpdate,
  onSessionEnd,
  guestMode = false,
  onGuestQuotaExhausted,
}) => {
  const [messages, setMessages] = useState<ChatMessageType[]>(session.messages || []);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(() => {
    const hasOnlyWelcomeMessage =
      session.messages?.length === 1 && session.messages[0]?.role === 'assistant';
    const isRecentlyCreated =
      new Date().getTime() - new Date(session.startedAt).getTime() < 60000;

    if (hasOnlyWelcomeMessage && isRecentlyCreated) {
      return true;
    }

    return (
      session.status === InterviewSessionStatus.Active ||
      session.status === 'Active' ||
      session.status === 'active'
    );
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const surface = guestMode ? 'try_interview' : 'interview_training';
    analytics.trackEvent({
      category: EventCategories.CHAT,
      action: EventActions.AI_CHAT_OPEN,
      label: surface,
      customParameters: {
        interview_type: String(session.interviewType),
        experience_level: String(session.experienceLevel),
      },
    });
    // One "open" per backend interview session, not on every message/session patch
  }, [session.sessionId, guestMode]);

  useEffect(() => {
    setMessages(session.messages || []);

    const hasOnlyWelcomeMessage =
      session.messages?.length === 1 && session.messages[0]?.role === 'assistant';
    const isRecentlyCreated =
      new Date().getTime() - new Date(session.startedAt).getTime() < 60000;

    let isActive;
    if (hasOnlyWelcomeMessage && isRecentlyCreated) {
      isActive = true;
    } else {
      const isStatusActive =
        session.status === InterviewSessionStatus.Active ||
        session.status === 'Active' ||
        session.status === 'active';
      isActive = isStatusActive;
    }

    setIsSessionActive(isActive);
  }, [session.messages, session.status, session.startedAt]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageContent: string) => {
    if (!isSessionActive) return;

    setIsLoading(true);

    const userMessage: ChatMessageType = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      const request: ChatRequest = {
        message: messageContent,
        sessionId: session.sessionId,
        interviewType: session.interviewType,
        jobRole: session.jobRole,
        companyName: session.companyName,
        experienceLevel: session.experienceLevel,
        conversationHistory: messages.slice(-10),
      };

      const response = guestMode
        ? await chatService.sendGuestMessage(session.sessionId, messageContent)
        : await chatService.sendMessage(request);

      if (response.isError) {
        const errText = response.errorMessage || response.message || 'Something went wrong.';
        const errMessage: ChatMessageType = {
          role: 'assistant',
          content: errText,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev.slice(0, -1), errMessage]);
        return;
      }

      const aiMessage: ChatMessageType = {
        role: 'assistant',
        content: response.message,
        timestamp:
          typeof response.timestamp === 'string' ? response.timestamp : new Date().toISOString(),
        metadata: response.metadata,
      };

      setMessages((prev) => [...prev, aiMessage]);

      const updatedSession = {
        ...session,
        messages: [...messages, userMessage, aiMessage],
      };
      onSessionUpdate(updatedSession);

      if (guestMode && response.guestQuotaExhausted) {
        setIsSessionActive(false);
        onGuestQuotaExhausted?.();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      let text = 'I apologize, but I encountered an error. Please try again.';
      if (
        guestMode &&
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object'
      ) {
        const d = error.response.data as { message?: string; errorMessage?: string };
        text = d.errorMessage || d.message || text;
      }
      const errorMessage: ChatMessageType = {
        role: 'assistant',
        content: text,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (guestMode) {
      setIsSessionActive(false);
      onSessionEnd(null);
      return;
    }
    try {
      const summary = await chatService.endInterview(session.sessionId);
      setIsSessionActive(false);
      onSessionEnd(summary);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  const getSessionDuration = () => {
    const start = new Date(session.startedAt);
    const now = new Date();
    const diff = now.getTime() - start.getTime();
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min`;
  };

  const getQuestionCount = () => {
    return messages.filter((m) => m.role === 'user').length;
  };

  return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-stitch-xl border border-stitch-border bg-stitch-surface shadow-stitch-pop">
      {/* Header */}
      <div className="shrink-0 rounded-t-stitch-xl bg-gradient-to-r from-stitch-primary to-stitch-accent p-4 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{session.interviewType} Interview</h2>
            <p className="text-sm opacity-90">
              {session.jobRole && `${session.jobRole} • `}
              {session.companyName && `${session.companyName} • `}
              {session.experienceLevel} Level
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-1 text-sm">
              <span className="material-symbols-outlined text-base">schedule</span>
              <span>{getSessionDuration()}</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              <span className="material-symbols-outlined text-base">bar_chart</span>
              <span>{getQuestionCount()} Q</span>
            </div>
            {isSessionActive && !guestMode ? (
              <Button type="button" variant="danger" size="sm" onClick={handleEndSession}>
                <span className="material-symbols-outlined text-base">stop_circle</span>
                <span>End Session</span>
              </Button>
            ) : isSessionActive && guestMode ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleEndSession}
                className="!text-white hover:!bg-stitch-surface/20"
              >
                Leave demo
              </Button>
            ) : (
              <div className="flex items-center gap-1 rounded-stitch-md bg-stitch-success px-3 py-1 text-sm">
                <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: "'FILL' 1" }}>
                  check_circle
                </span>
                <span>Completed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto scroll-smooth overscroll-y-contain p-4 pb-6 [scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.6)_transparent]">
        {messages.length === 0 ? (
          <div className="mt-8 text-center text-stitch-muted">
            <div className="rounded-stitch-xl border border-stitch-border bg-stitch-surface-2 p-6">
              <span className="material-symbols-outlined mx-auto mb-4 block text-5xl text-stitch-muted">play_circle</span>
              <h3 className="mb-2 text-lg font-medium text-stitch-fg">Ready to Start!</h3>
              <p className="text-sm text-stitch-fg-soft">
                Your AI interviewer is ready. Type your first response to begin the interview.
              </p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => <ChatMessage key={index} message={message} />)
        )}

        {isLoading && (
          <div className="mb-4 flex justify-start">
            <div className="max-w-[80%] rounded-stitch-lg border border-stitch-border bg-stitch-surface-2 px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
                <span className="text-sm text-stitch-fg-soft">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={!isSessionActive || isLoading}
        placeholder={isSessionActive ? 'Type your response...' : 'Session has ended'}
      />
    </div>
  );
};
